import { Box, Button, Typography, useMediaQuery, useTheme } from "@mui/material";
import Grid from "@mui/material/Grid2";
import "./ProjectSubSectionsPage.css";
import React, { useEffect, useState } from "react";
import StagesProgressComponent from "../../Components/StagesProgressComponent/StagesProgressComponent";
import { useLocation, useOutletContext, useParams } from "react-router-dom";
import { getSectionSubsections, getSectionSubsectionsList, getStageSections } from "../../Services/api";
import LoaderComponent from "../../Components/LoaderComponent/LoaderComponent";
import SubSectionsComponent from "../../Components/SubSectionsComponent/SubSectionsComponent";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import HorizontalLegendComponent from "../../Components/HorizontalLegendComponent/HorizontalLegendComponent";
import SubSectionsCardsComponent from "../../Components/SubSectionCardsComponent/SubSectionsCardsComponent";
import ProjectStatusPanelComponent from "../../Components/VerticalLegendComponent/VerticalLegendComponent";

interface Props {}

const ProjectSubSectionsPage = (props: Props) => {
  const { id, stageIndex, sectionIndex } = useParams();
  const { state } = useLocation();
  const { hideDelays, hideLegend, stages } = useOutletContext<any>();

  const [stageSections, setStageSections] = useState<any>(null);
  const [subSections, setSubSections] = useState<any>(null);

  const [subSectionsList, setSubSectionsList] = useState<any>(null);
  const [isLoadingSubSectionsList, setIsLoadingSubSectionsList] = useState<any>(null);

  const [isSubSectionsListView, setIsSubSectionsListView] = useState(false);

  const [selectedStageIndex, setSelectedStageIndex] = useState(0);
  const [selectedSectionIndex, setSelectedSectionIndex] = useState(0);

  const [loading, setLoading] = useState(true);
  const [subLoading, setSubLoading] = useState(false);
  const [error, setError] = useState(null);

  const { setBreadcrumbs, setBackButton } = useOutletContext<any>();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  useEffect(() => {
    setSelectedStageIndex(Number(stageIndex));
    setSelectedSectionIndex(Number(sectionIndex));
    setBreadcrumbs([
      { name: 'לו"ז המיזם', path: "/project/details/" + id },
      { name: "מקטעים", path: "/project/sections/" + id + "/" + stageIndex },
      { name: "תתי מקטעים", path: "#" },
    ]);
    setBackButton({ title: "מקטעים", route: `/project/sections/${id}/${stageIndex}` });

    if (state && state.sections) {
      setStageSections(state.sections);
      loadSubSections(stages[Number(stageIndex)].id_number, state.sections[Number(sectionIndex)].id_number);
      setLoading(false);
    } else {
      Promise.all([getStageSections(id, stages[Number(stageIndex)].id_number), Promise.resolve(stages[Number(stageIndex)].id_number)])
        .then(([sectionsData, stage_id_number]) => {
          setStageSections(sectionsData.sections);

          return getSectionSubsections(id, stage_id_number, sectionsData.sections[Number(sectionIndex)].id_number);
        })
        .then((subSectionsData) => {
          setSubSections(subSectionsData.subsections);
          setLoading(false);
        })
        .catch((err) => {
          setError(err.message || "Unknown error");
        })
        .finally(() => {});
    }

    const resetFocus = document.getElementById("reset-focus");
    if (resetFocus) resetFocus.focus();
  }, [id, setBreadcrumbs, setBackButton]);

  const loadSections = async (stageId: number) => {
    setSubLoading(true);
    setError(null);

    getStageSections(id, stageId)
      .then((data) => {
        setStageSections(data.sections);
        setSubLoading(false);
      })
      .catch((err) => {
        setError(err.message || "Unknown error");
      })
      .finally(() => {});
  };

  const loadSubSections = async (stageId: number, sectionId: number) => {
    setSubLoading(true);
    setError(null);

    getSectionSubsections(id, stageId, sectionId)
      .then((data) => {
        setSubSections(data.subsections);
        setSubLoading(false);
      })
      .catch((err) => {
        setError(err.message || "Unknown error");
      })
      .finally(() => {});
  };

  const loadSubSectionsList = async () => {
    setIsLoadingSubSectionsList(true);
    setError(null);

    getSectionSubsectionsList(id, stages[selectedStageIndex].id_number)
      .then((data) => {
        setSubSectionsList(data);
        setIsLoadingSubSectionsList(false);
      })
      .catch((err) => {
        setError(err.message || "Unknown error");
      })
      .finally(() => {});
  };

  if (loading) return <LoaderComponent />;
  // if (error) return <p>Error: {error}</p>;

  const onChangeStage = (index: number) => {
    setSelectedStageIndex(index);
    loadSections(stages[index].id_number);
  };

  const onChangeStageSection = (index: number) => {
    setSelectedSectionIndex(index);
    loadSubSections(stages[selectedStageIndex].id_number, stageSections[index].id_number);
    loadSubSectionsList();
  };

  const toggleListView = () => {
    setIsSubSectionsListView(!isSubSectionsListView);
    loadSubSectionsList();
  };

  return (
    <Box>
      {!hideLegend && isMobile && (
        <Grid size={{ xs: 12, md: 2 }}>
          <ProjectStatusPanelComponent />
        </Grid>
      )}
      {!isMobile && <HorizontalLegendComponent />}

      <Typography variant={"h2"} textAlign={"center"} mb={3} className="project-details-breadcrumbs">
        מקטעים של שלב {stages[selectedStageIndex].name}
      </Typography>

      {subLoading ? (
        <LoaderComponent fullScreen={false} />
      ) : (
        <div className="project-sections-container">
          <Grid container spacing={2} mb={10} justifyContent="center">
            <Grid container spacing={2} size={10}>
              <Grid size={12}>
                <StagesProgressComponent
                  stages={stageSections}
                  selectedIndex={selectedSectionIndex}
                  hideDelays={hideDelays}
                  onChangeStage={onChangeStageSection}
                  compact={true}
                />

                {isSubSectionsListView ? (
                  <SubSectionsComponent
                    onClose={toggleListView}
                    title={"רשימת המקטעים של " + stages[selectedStageIndex].name}
                    data={subSectionsList}
                    isLoading={isLoadingSubSectionsList}
                  />
                ) : (
                  <div>
                    <Box
                      display={"flex"}
                      justifyContent={"center"}
                      alignItems={"center"}
                      gap={3}
                      flexDirection={isMobile ? "column" : "row"}
                      marginBottom={5}
                    >
                      <Typography variant={"h3"} textAlign={"right"} marginLeft={"50px"} className="project-details-breadcrumbs">
                        תתי מקטעים של שלב {stageSections[selectedSectionIndex].name}
                      </Typography>

                      <Button
                        onClick={toggleListView}
                        variant="outlined"
                        fullWidth
                        endIcon={<ExpandMoreIcon />}
                        sx={{
                          borderRadius: "16px",
                          textTransform: "none",
                          justifyContent: "space-between",
                          py: 1.5,
                          borderColor: "rgba(0,0,0,0.23)",
                          color: "rgba(0,0,0,0.87)",
                          backgroundColor: "#FFFFFF",
                          width: isMobile ? "100%" : "450px",
                        }}
                      >
                        רשימת המקטעים של {stages[selectedStageIndex].name}
                      </Button>
                    </Box>

                    <SubSectionsCardsComponent subSections={subSections} hideDelays={hideDelays} />
                  </div>
                )}
              </Grid>
            </Grid>
          </Grid>
        </div>
      )}
    </Box>
  );
};

export default ProjectSubSectionsPage;
